import { Row, Card } from 'antd'

import DeserveStyle from '../../styles/DriverDeserveTransparency.module.css'
import { SEO } from './components'

const DriverDeserveTransparency = () => {
  return (
    <>
      <SEO
        title={"All New Jersey Drivers Deserve Transparency and Trust | CURE"} 
        description={"The article reveals how laws meant to protect lower-income drivers from unfair insurance discrimination have lost their effectiveness and impact."} 
        url={"/nm/news/driver-deserve-transparency"}
      />
      <div className={`${DeserveStyle["drive-deserve-transparency-screen"]}`}>
        <Row justify="center">
          <Card className={`${DeserveStyle["drive-deserve-card"]}`}>
            <article>
              <span>SEPTEMBER 20, 2021 &nbsp;|&nbsp;NJLJ.COM</span>
              <h1 className={`${DeserveStyle["drive-deserve-title"]}`}>All New Jersey Drivers Deserve Transparency and Trust</h1>
              <h4 className={`${DeserveStyle["blog-sub-title"]}`}>
                The author points to several laws that were clearly created to help protect lower income drivers and
                prevent car insurers from surreptitiously and unfairly discriminating against unsuspecting consumers, but
                which simply no longer have any &quot;teeth.&quot;
              </h4>
              <p className={`${DeserveStyle["drive-deserve-author-name"]}`}>By Eric S. Poe</p>
              <hr />
              <figure className={`${DeserveStyle["right"]}`}>
                <img alt="Eric Poe - CURE auto insurance" src="https://assets.cure.com/img/main-site/Eric.png" />
                <figcaption>Eric S. Poe, Esq., CPA</figcaption>
              </figure>
              <p>
                Over the past couple of months, I unmasked{' '}
                <a
                  className={`${DeserveStyle["drive-deserve-hyperlinks"]}`}
                  href="https://www.law.com/njlawjournal/2021/07/09/a-dirty-little-secret-in-car-insurance-can-lawyers-lead-a-change/"
                  rel="noreferrer"
                  target="_blank"
                >
                  &quot;A Dirty Little Secret in Car Insurance&quot;
                </a>{' '}
                (N.J.L.J., July 12, 2021) and spoke of a{' '}
                <a
                  className={`${DeserveStyle["drive-deserve-hyperlinks"]}`}
                  href="https://www.law.com/njlawjournal/2021/07/30/the-half-pregnant-public-policy/"
                  rel="noreferrer"
                  target="_blank"
                >
                  &quot;Half-Pregnant Public Policy&quot;
                </a>{' '}
                (N.J.L.J., Aug. 2, 2021)—all to bring awareness to how income proxies have been used in the New Jersey car
                insurance marketplace, secretly harming many safe drivers simply because they happen to have the lowest
                incomes. While I have personally crusaded against these proxies now for over the past 16 years, I felt I
                should also shed light on other New Jersey insurance laws that were originally meant to protect consumers
                that have also been eroded. Specifically, I point to several laws that were clearly created to help
                protect lower income drivers and prevent car insurers from surreptitiously and unfairly discriminating
                against unsuspecting consumers, but which simply no longer have any &quot;teeth.&quot;
              </p>

              <h5>The Protection Against &apos;Unfairly Discriminatory&apos; Rates</h5>

              <figure className={`${DeserveStyle["left"]}`}>
                <img
                  alt="Unfairly Discriminatory Auto Insuance Rates"
                  className={`${DeserveStyle["drive-deserve-transparency-left-img"]}`}
                  src="https://assets.cure.com/img/main-site/Red-car.jpeg"
                />
              </figure>
              <p>
                Like most other states that mandate car insurance, the New Jersey legislature has language in place to
                protect consumers from rating factors that are &quot;unfairly discriminatory.&quot;
              </p>
              <p>Specifically, at N.J.S.A. 17:29A-7, New Jersey&apos;s rating law reads in part:</p>
              <p>
                If ... the commissioner shall find that such rating-systems ... provide for, result in, or produce rates
                that are ... unfairly discriminatory between risks in this State involving essentially the same hazards
                and expense elements, he shall issue an order ... directing that such rating-systems be altered ... to
                produce rates that are reasonable and adequate, and not unfairly discriminatory.
              </p>
              <p>
                While the words on paper may be black and white, the question &quot;What is considered to be unfairly
                discriminatory?&quot; has been creatively redefined over the years. How could factors such as education,
                occupation, and credit score, be deemed fair when all one needs to do is pick up the most recent U.S.
                Census to see a clear link between these factors and income.
              </p>

              <p>
                So, how did my fellow car insurers get these factors approved with this law in place? By showing the link
                to profitability and their use. If the regulators believe a factor has a proven correlation to loss ratios
                (a profit measurement), the factors can be &quot;fair&quot; and therefore the regulators approve them.
              </p>

              <h5>Let&apos;s Start With the Basics</h5>

              <p>
                The purpose of insurance is to protect against a future unpredictable loss and share such risks among a
                group of other members so that one unfortunate event, whether a car accident, fire or other casualty, will
                not financially devastate any one individual. Instead, the pooling of these risks allow affordable
                protection to be spread across an entire group. This is a centuries-old concept dating back to Chinese
                merchants who sought a way to protect against financial ruin if their ship were to sink in transit.
                Therefore, in essence, the best measurement of a successful mandated insurance market is the ability for
                everyone to afford a required insurance policy.
              </p>

              <p>
                Against this backdrop, 20 years ago New Jersey looked to &quot;help bring those uninsured [drivers] into
                market by giving them a lower cost option&quot; and mandated that all insurance carriers in the state
                offer a &quot;Basic&quot; policy. The rationale behind creating the basic policy was clear: &quot;It is
                anticipated that individuals purchasing this policy would be those with few tangible assets to protect as
                well as lower-income persons who cannot now purchase insurance because it is unaffordable.&quot; To
                paraphrase further, the legislators saw this as a way to reduce the number of uninsured drivers by
                offering less coverage at a lower cost. See N.J.S.A. 39:6A-3.1 and N.J.A.C. 11:3-3.3.
              </p>

              <p>
                This all sounds good and, might I say, reasonable, right? If we require insurance companies to sell a
                minimum of 10 apples and they charge $10.00 for those apples, by reducing the required minimum to five,
                the cost to would change and be reduced to $5.00. You can&apos;t get more straightforward than this
                approach.
              </p>
              <p>
                Unfortunately, the well-intentioned goals did not materialize due to a major flaw in the process of
                implementation. Because at that time the regulators allowed the car insurance companies to re-file
                different and higher rates for Basic policies. So, despite a car insurance company offering less coverage
                than a standard policy, insurers were allowed to charge more for it, which negated the cost saving effects
                of the law.
              </p>

              <h5>The Right for Public Inspection of Car Insurance Rate Filings</h5>

              <p>
                When one uses the word &quot;transparent&quot; in business, it is meant to relay a sense of openness and
                candor. In establishing laws, a major concern is when the government creates a requirement of its citizens
                and then allows privatized, for-profit companies to service that directive.
              </p>

              <p>
                This is clearly the case in New Jersey when it comes to requiring that all drivers carry car insurance.
                The state&apos;s law on public records as it relates to insurance underwriting rules is rather clear.
                Since 1997, in accordance with New Jersey law, all underwriting rules shall be &quot;subject to public
                inspection.&quot; See N.J.S.A. 17:29A-46.2. A clarification of this law occurred when the New Jersey
                Department of Banking and Insurance (DOBI) went on to address concerns of confidentiality in filings
                related to how an insurer determined whether to decline or non-renew a risk. &quot;Insureds must be
                provided with the rationale and the basis for such a determination. Accordingly, an insurer may be called
                upon to provide a copy of its underwriting rules. In any event, the Department does not believe it
                appropriate to define by this rule that an insurer&apos;s alternate underwriting rules are
                confidential.&quot; See New Jersey Register, 36 N.J. Reg. 1929(a).
              </p>

              <p>
                Despite the clear right to public inspection of all underwriting rules, in April 2006, in an attempt to
                bring transparency to the underwriting process, New Jersey Senator Nia Gill requested documents from DOBI,
                through the New Jersey Open Public Records Act (OPRA), pertaining to the use of occupation and education
                as underwriting factors by GEICO and other automobile insurance carriers. DOBI, at the time, denied the
                request, arguing that despite N.J.S.A. 17:29A-46.2, the documents contained proprietary information and
                would provide an unfair advantage to the insurer&apos;s competitors. Failing to resolve the dispute with
                DOBI, Senator Gill filed a complaint with the Government Records Council. Following a full hearing, the
                Administrative Law Judge determined that the disputed documents were not underwriting rules and therefore
                were not subject to public inspection, a decision ultimately upheld by the Appellate Division in an
                unpublished opinion. <i>Gill v. New Jersey Dept of Banking & Ins</i>., A-1801-10T4, 2013 WL 534786 (App.
                Div. Feb. 14, 2013).
              </p>
              <p>
                Amid all this, in 2008, DOBI adopted a new regulation prohibiting the use of &quot;occupation, education
                or insurance score of the applicant&quot; in acceptance criteria of auto carriers. N.J.A.C. 11:3-8.12. In
                doing so, they broke the category of &quot;underwriting&quot; into two separate and fictitious buckets of
                acceptance and rating. As everyone in the insurance industry understands and acknowledges, these are two
                parts of the same process.
              </p>

              <p>
                If DOBI regulators in 2008 recognized that these income-related factors have no &quot;fair&quot; place in
                at least part of the insurance application and rejection process, then why allow them at all? It seems
                like a distinction without a difference. Common sense suggests that it makes no practical difference to a
                consumer if car insurance companies are prohibited to reject you based on your lack of a four-year college
                degree alone, but they are permitted to charge you 110% more for not having it. That&apos;s what I refer
                to as a &quot;half-pregnant&quot; public policy.
              </p>

              <h5>It Begs the Question</h5>

              <p>
                Why is this still happening today? I am not alone in asking this question, and it appears those in the
                position to right this wrong are listening. It&apos;s time to cross the finish line and give real meaning
                to &quot;unfairly discriminatory&quot; by ending these practices.
              </p>

              <hr />
              <p className={`${DeserveStyle["author"]}`}>
                <span className={`${DeserveStyle["bold"]}`}>Eric S. Poe,</span> attorney and CPA, is principal and serves as chief executive
                officer and complex claims litigation officer for Citizens United Reciprocal Exchange (CURE) auto
                insurance, and New Jersey Physicians United Reciprocal Exchange (NJ PURE), a leading medical malpractice
                insurance carrier in the state.
              </p>

              <hr />
              <p className={`${DeserveStyle["drive-deserve-foot-note"]}`}>
                Reprinted with permission from the September 20, 2021 edition of the NEW JERSEY LAW JOURNAL.© 2021 ALM
                Media Properties, LLC. All rights reserved. Further duplication without permission is prohibited. For
                information, contact 877-256-2472, reprints@alm.com or visit{' '}
                <a className={`${DeserveStyle["drive-deserve-hyperlinks"]}`} href="https://almreprints.com/" rel="noreferrer" target="_blank">
                  www.almreprints.com
                </a>
                . NJLJ-09202021-520434
              </p>
            </article>
          </Card>
        </Row>
      </div>
    </>
  )
}

export default DriverDeserveTransparency
